import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {PrimaryButton} from "@/components/Button";
import useGetUserWithdrawalLimit from "../../../../hooks/wallet/useGetUserWithdrawalLimit";
import {formatBalanceWithCurrency} from "../../../../util/balance.util";
import styles from "./WithdrawalLimits.module.css";

const RING_RADIUS = 30;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const PercentageRing = ({percent = 0}) => {
  const offset = RING_CIRCUMFERENCE - (percent / 100) * RING_CIRCUMFERENCE;
  return (
    <div className={styles.percentageRing}>
      <svg
        viewBox="0 0 72 72"
        width="72"
        height="72"
        aria-hidden="true"
      >
        <circle
          className={styles.ringTrack}
          cx="36"
          cy="36"
          r={RING_RADIUS}
        />
        <circle
          className={styles.ringProgress}
          cx="36"
          cy="36"
          r={RING_RADIUS}
          strokeDasharray={RING_CIRCUMFERENCE}
          strokeDashoffset={offset}
        />
      </svg>
      <span className={styles.percentageValue}>{percent}%</span>
    </div>
  );
};

const WithdrawalLimits = () => {
  const {data, isLoading} = useGetUserWithdrawalLimit();
  const [showLimitWarning, setShowLimitWarning] = useState(false);

  const {t} = useTranslation();

  const dailyPercent = Math.floor(data?.DailyPercentage) || 0;
  const weeklyPercent = Math.floor(data?.WeeklyPercentage) || 0;

  const dailyWithdrawal = formatBalanceWithCurrency(data?.WithdrawnToday);
  const weeklyWithdrawal = formatBalanceWithCurrency(data?.WithdrawnThisWeek);

  const dailyRemaining = formatBalanceWithCurrency(data?.RemainingDaily);
  const weeklyRemaining = formatBalanceWithCurrency(data?.RemainingWeekly);

  useEffect(() => {
    if (dailyPercent === 100 || weeklyPercent === 100) {
      const timer = setTimeout(() => setShowLimitWarning(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [dailyPercent, weeklyPercent]);

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner} />
      </div>
    );
  }

  return (
    <>
      {showLimitWarning && (
        <div
          className={styles.limitCard}
          style={{marginBottom: 16, border: "1px solid rgba(239, 68, 68, 0.25)"}}
        >
          {dailyPercent === 100 && <p className={styles.limitTitle}>{t("withdrawalDayModalLimitReachedMessage")}</p>}
          {weeklyPercent === 100 && <p className={styles.limitTitle}>{t("withdrawalWeekModalLimitReachedMessage")}</p>}
          <PrimaryButton
            onClick={() => setShowLimitWarning(false)}
            style={{width: "100%", marginTop: 8}}
          >
            {t("withdrawalStepsModalLimitReachedButton")}
          </PrimaryButton>
        </div>
      )}

      <div className={styles.limitsContainer}>
        <div className={styles.limitCard}>
          <PercentageRing percent={dailyPercent} />
          <p className={styles.limitTitle}>{t("withdrawalStepsDailyWithdrawal")}</p>
          <div style={{width: "100%"}}>
            <div className={styles.limitDataRow}>
              <span className={styles.limitDataLabel}>{t("withdrawalStepsWithdrawn")}:</span>
              <span className={styles.limitDataValue}>{dailyWithdrawal}</span>
            </div>
            <div className={styles.limitDataRow}>
              <span className={styles.limitDataLabel}>{t("withdrawalStepsDailyRemaining")}:</span>
              <span className={styles.limitDataValue}>{dailyRemaining}</span>
            </div>
          </div>
        </div>

        <div className={styles.limitCard}>
          <PercentageRing percent={weeklyPercent} />
          <p className={styles.limitTitle}>{t("withdrawalStepsWeeklyWithdrawal")}</p>
          <div style={{width: "100%"}}>
            <div className={styles.limitDataRow}>
              <span className={styles.limitDataLabel}>{t("withdrawalStepsWithdrawn")}:</span>
              <span className={styles.limitDataValue}>{weeklyWithdrawal}</span>
            </div>
            <div className={styles.limitDataRow}>
              <span className={styles.limitDataLabel}>{t("withdrawalStepsWeeklyRemaining")}:</span>
              <span className={styles.limitDataValue}>{weeklyRemaining}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WithdrawalLimits;
